import React, { useState, useEffect, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { isEmpty, uniq } from 'ramda';

// store
import { storeSet } from '../../../../store';

// api
import AssignedCompanyService from '../../../../service/assigned-company-service';
import UserService from '../../../../service/user-service';

// tools
import set404FromErrorResponse from '../../../../helpers/set404FromErrorResponse';

// components
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import ProperBandoLabel from '../../../../components/ProperBandoLabel';
import { classNames } from 'primereact/utils';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import translationStrings from '../../../../translationStringsForComponents';
import { Link } from 'react-router-dom';
import getBandoLabel from '../../../../helpers/getBandoLabel';
import getBandoSeverity from '../../../../helpers/getBandoSeverity';
import { Tag } from 'primereact/tag';


const ListCompaniesTable = () => {
    const [items, setItems] = useState(null);
    const [filters, setFilters] = useState(null);
    const [loading, setLoading] = useState(false);
    const [chosenCompany, setChosenCompany] = useState(0);
    const [chosenUser, setChosenUser] = useState(0);
    const [isVisibleAssignDialog, setIsVisibleAssignDialog] = useState(false);
    const toast = useRef(null);
    const [users, setUsers] = useState([]);
    const [roleIds, setRoleIds] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [lookUpData, setLookUpData] = useState([]);

    useEffect(() => {
        setLoading(true);

        AssignedCompanyService.getAssignedCompanies(getCallback, errGetCallbacks);
    }, []);

    const getCallback = (resp) => {
        if (resp.status === 'SUCCESS') {
            setItems(getFormattedData(resp.data));
            setStatuses(uniq(resp.data.map(o => o.companyStatus)))
            setLookUpData(uniq(resp.data.map(o => o.lookupDataValue)))
            initFilters();
        }
        setLoading(false);
    }

    const errGetCallbacks = (data) => {
        set404FromErrorResponse(data);
        setLoading(false);
    }

    useEffect(() => {
        if (isVisibleAssignDialog) {
            setLoading(true);
            UserService.getRoles(getRolesCallback, errGetRolesCallback)
        }
    }, [isVisibleAssignDialog]);

    const getRolesCallback = (data) => {
        if (data.status === 'SUCCESS') {
            const roles = data.data
                .filter(o => ['ROLE_PRE_INSTRUCTOR', 'ROLE_INSTRUCTOR_MANAGER'].includes(o.roleType));
            setRoleIds(roles.map(o => o.id));
        }
        setLoading(false);
    }

    const errGetRolesCallback = (data) => {
        set404FromErrorResponse(data);
        setLoading(false);
    }

    useEffect(() => {
        if (roleIds.length > 0) {
            setLoading(true);
            UserService.getUsers(getUsersCallback, errGetUsersCallback, [['roleIds', roleIds]])
        }
    }, [roleIds]);

    const getUsersCallback = (data) => {
        if (data.status === 'SUCCESS') {
            const users = data.data
                .map(o => ({
                    name: `${o.firstName} ${o.lastName} (${o.email})`,
                    value: o.id
                }));
            setUsers(users);
        }
        setLoading(false);
    }

    const errGetUsersCallback = (data) => {
        set404FromErrorResponse(data);
        setLoading(false);
    }

    const getFormattedData = (data) => {
        return [...(data || [])].map((d) => {
            d.createdDate = new Date(d.createdDate);
            d.updatedDate = new Date(d.updatedDate);

            return d;
        });
    };

    const formatDate = (value) => {
        return value.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            companyId: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            companyName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            supplierName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            companyStatus: {
                operator: FilterOperator.OR,
                constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
            },
            lookupDataValue: {
                operator: FilterOperator.OR,
                constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
            },
            createdDate: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
            },
            assignedToUserName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
        });
    };

    const renderHeader = () => {
        return (
            <div className="appTableHeader">
                <Button type="button" icon="pi pi-filter-slash" label={__('Pulisci', 'htr')} outlined
                        onClick={clearFilter}/>
            </div>
        );
    };

    const dateStartBodyTemplate = (rowData) => {
        return formatDate(rowData.createdDate);
    };

    const dateFilterTemplate = (options) => {
        return <Calendar value={options.value} onChange={(e) => options.filterCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy" placeholder="dd/mm/yyyy" mask="99/99/9999"/>;
    };

    const statusBodyTemplate = (rowData) => {
        return <ProperBandoLabel status={rowData.companyStatus}/>;
    };

    const lookUpBodyTemplate = (rowData) => {
        return rowData.lookupDataValue;
    };

    const actionsBodyTemplate = (rowData) => {
        return <div className="appPageSection__tableActions">
            {rowData.companyStatus !== 'APPROVED' && !rowData.assignedToUserId
                ? <Button severity="info"
                    onClick={() => openAssignDialog(rowData.companyId)}
                    label={__('Assegna', 'htr')}
                    icon="pi pi-pencil" size="small"
                    iconPos="right"/> : null}
            {rowData.assignedToUserId
                ? <Link to={`/fornitori/${rowData.companyId}`}>
                <Button severity="info"
                        label={__('Mostra', 'htr')}
                        icon="pi pi-arrow-right" size="small"
                        iconPos="right"/>
            </Link> : null}
        </div>
    }

    const lookUpFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={lookUpData}
                         valueTemplate={options.value}
                         onChange={(e) => options.filterCallback(e.value, options.index)}
                         itemTemplate={lookUpItemTemplate} placeholder={__('Scegli uno', 'htr')}
                         className="p-column-filter"/>;
    };

    const lookUpItemTemplate = (option) => {
        return option;
    };

    const statusFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={statuses}
                         valueTemplate={getBandoLabel(options.value)}
                         onChange={(e) => options.filterCallback(e.value, options.index)}
                         itemTemplate={statusItemTemplate} placeholder={__('Scegli uno', 'htr')}
                         className="p-column-filter"/>;
    };

    const statusItemTemplate = (option) => {
        return <Tag value={getBandoLabel(option)} severity={getBandoSeverity(option)}/>;
    };

    const header = renderHeader();

    const openAssignDialog = (companyId) => {
        setChosenCompany(companyId)
        setIsVisibleAssignDialog(true);
    }

    const headerAssignDialog = () => {
        return <span>{__('Assegni la domanda', 'htr')}</span>
    }

    const hideAssignDialog = () => {
        setIsVisibleAssignDialog(false);
        setChosenUser(0);
        setChosenCompany(0);
    }

    const footerAssignDialog = () => {
        return <div>
            <Button type="button" label={__('Annulla', 'htr')} onClick={hideAssignDialog} outlined/>
            <Button
                type="button"
                disabled={loading}
                label={__('Invia', 'htr')} onClick={doAssign}/>
        </div>
    }

    const doAssign = () => {
        if (chosenUser !== 0 && chosenCompany !== 0) {
            storeSet('setAsyncRequest');

            AssignedCompanyService.assignCompany(chosenCompany, assignCallback, errAssignCallback, [
                ['userId', chosenUser]
            ]);
            hideAssignDialog();
        }
    }

    const assignCallback = (data) => {
        if (data.status === 'SUCCESS') {
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: data.message
                });
            }
        }
        storeSet('unsetAsyncRequest');
    }

    const errAssignCallback = (data) => {
        if (toast.current && data.message) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: data.message
            });
        }
        set404FromErrorResponse(data);
        storeSet('unsetAsyncRequest');
    }

    return (
        <div className="appPageSection__table">
            <Toast ref={toast}/>

            <DataTable value={items} paginator showGridlines rows={5} loading={loading} dataKey="id"
                       filters={filters} stripedRows removableSort
                       header={header}
                       emptyMessage={translationStrings.emptyMessage}
                       onFilter={(e) => setFilters(e.filters)}>
                <Column field="companyId" header={__('ID Reg', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca il ID', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column field="supplierName" header={__('Nome', 'htr')}
                           filter sortable
                           filterPlaceholder={__('Cerca il nome', 'htr')}
                           style={{ minWidth: '8rem' }}/>
                <Column field="companyName" header={__('Azienda', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca la azienda', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column field="lookupDataValue" header={__('Categoria', 'htr')} filterMenuStyle={{ width: '14rem' }}
                        style={{ minWidth: '7rem' }} body={lookUpBodyTemplate}
                        filter sortable
                        filterElement={lookUpFilterTemplate}/>
                <Column header={__('Invio Documenti', 'htr')} filterField="createdDate" dataType="date"
                        style={{ minWidth: '8rem' }}
                        body={dateStartBodyTemplate} filter filterElement={dateFilterTemplate}/>
                <Column field="companyStatus" header={__('Stato', 'htr')} filterMenuStyle={{ width: '14rem' }}
                        style={{ minWidth: '7rem' }} body={statusBodyTemplate}
                        filter sortable
                        filterElement={statusFilterTemplate}/>
                <Column field="assignedToUserName" header={__('Assegnato a', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca il nome', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column header={__('Azioni', 'htr')}
                        body={actionsBodyTemplate}/>
            </DataTable>

            <Dialog
                visible={isVisibleAssignDialog}
                modal
                header={headerAssignDialog}
                footer={footerAssignDialog}
                style={{ maxWidth: '600px', width: '100%' }}
                onHide={hideAssignDialog}>
                <div className="appForm__field">
                    <label
                        className={classNames({ 'p-error': isEmpty(chosenUser) || chosenUser === 0 || chosenCompany === 0 })}>
                        {__('Operatore HTR', 'htr')}*
                    </label>
                    <Dropdown
                        value={chosenUser}
                        invalid={isEmpty(chosenUser) || chosenUser === 0 || chosenCompany === 0}
                        onChange={(e) => setChosenUser(e.value)}
                        options={users}
                        optionLabel="name"
                        optionValue="value"/>
                </div>
            </Dialog>
        </div>
    )
}

export default ListCompaniesTable;